import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Store,
  Search,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Phone,
  Mail,
  MapPin,
  Building,
  CreditCard,
  Percent,
  ChevronDown,
  ChevronUp,
  Loader2,
  Users,
  Ban,
  Settings,
  IndianRupee,
  Calendar,
  Save
} from 'lucide-react';

export default function AdminVendors() {
  const [vendors, setVendors] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0, suspended: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');  // Default to all vendors
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedVendor, setExpandedVendor] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(null);
  const [showSettingsModal, setShowSettingsModal] = useState(null);
  const [settingsData, setSettingsData] = useState({
    commission_rate: 10,
    min_payout: 500,
    payout_schedule: 'weekly'
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchVendors();
  }, [filter]);

  const fetchVendors = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = filter !== 'all' ? { status: filter } : {};
      const response = await axios.get(`${API_URL}/api/admin/vendors`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setVendors(response.data.vendors || []);
      setCounts(response.data.counts || { pending: 0, approved: 0, rejected: 0, suspended: 0 });
    } catch (error) {
      console.error('Error fetching vendors:', error);
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (vendorId) => {
    setActionLoading(vendorId);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/admin/vendors/${vendorId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Vendor approved successfully!');
      fetchVendors();
    } catch (error) {
      console.error('Error approving vendor:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve vendor');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (vendorId) => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    setActionLoading(vendorId);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/admin/vendors/${vendorId}/reject`,
        { reason: rejectReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Vendor rejected');
      setShowRejectModal(null);
      setRejectReason('');
      fetchVendors();
    } catch (error) {
      console.error('Error rejecting vendor:', error);
      toast.error(error.response?.data?.detail || 'Failed to reject vendor');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSuspend = async (vendorId) => {
    setActionLoading(vendorId);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/admin/vendors/${vendorId}/suspend`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Vendor suspended');
      fetchVendors();
    } catch (error) {
      console.error('Error suspending vendor:', error);
      toast.error(error.response?.data?.detail || 'Failed to suspend vendor');
    } finally {
      setActionLoading(null);
    }
  };

  const openSettingsModal = (vendor) => {
    setSettingsData({
      commission_rate: vendor.commission?.rate || 10,
      min_payout: vendor.commission?.min_payout || 500,
      payout_schedule: vendor.commission?.payout_schedule || 'weekly'
    });
    setShowSettingsModal(vendor);
  };

  const handleSaveSettings = async () => {
    if (!showSettingsModal) return;
    
    setActionLoading(showSettingsModal.id);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/admin/vendors/${showSettingsModal.id}/commission`,
        {
          rate: parseFloat(settingsData.commission_rate),
          min_payout: parseFloat(settingsData.min_payout),
          payout_schedule: settingsData.payout_schedule
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Vendor settings updated!');
      setShowSettingsModal(null);
      fetchVendors();
    } catch (error) {
      console.error('Error updating vendor settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to update settings');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock },
      approved: { color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
      rejected: { color: 'bg-red-100 text-red-800 border-red-200', icon: XCircle },
      suspended: { color: 'bg-gray-100 text-gray-800 border-gray-200', icon: Ban }
    };
    const { color, icon: Icon } = config[status] || config.pending;
    return (
      <Badge className={`${color} border flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const filteredVendors = vendors.filter(vendor => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      vendor.business_name?.toLowerCase().includes(search) ||
      vendor.owner_name?.toLowerCase().includes(search) ||
      vendor.email?.toLowerCase().includes(search) ||
      vendor.phone?.includes(search)
    );
  });

  return (
    <div className="p-6 space-y-6" data-testid="admin-vendors-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Store className="w-7 h-7 text-green-600" />
            Vendor Management
          </h1>
          <p className="text-gray-500 mt-1">Review, manage, and configure marketplace vendors</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card 
          className={`cursor-pointer transition-all ${filter === 'all' ? 'ring-2 ring-blue-500' : ''}`}
          onClick={() => setFilter('all')}
          data-testid="all-filter"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">All Vendors</p>
                <p className="text-2xl font-bold text-blue-600">{counts.total || (counts.pending + counts.approved + counts.rejected + counts.suspended)}</p>
              </div>
              <Users className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all ${filter === 'pending' ? 'ring-2 ring-yellow-500' : ''}`}
          onClick={() => setFilter(filter === 'pending' ? 'all' : 'pending')}
          data-testid="pending-filter"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending</p>
                <p className="text-2xl font-bold text-yellow-600">{counts.pending}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all ${filter === 'approved' ? 'ring-2 ring-green-500' : ''}`}
          onClick={() => setFilter(filter === 'approved' ? 'all' : 'approved')}
          data-testid="approved-filter"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Approved</p>
                <p className="text-2xl font-bold text-green-600">{counts.approved}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all ${filter === 'rejected' ? 'ring-2 ring-red-500' : ''}`}
          onClick={() => setFilter(filter === 'rejected' ? 'all' : 'rejected')}
          data-testid="rejected-filter"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Rejected</p>
                <p className="text-2xl font-bold text-red-600">{counts.rejected}</p>
              </div>
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all ${filter === 'suspended' ? 'ring-2 ring-gray-500' : ''}`}
          onClick={() => setFilter(filter === 'suspended' ? 'all' : 'suspended')}
          data-testid="suspended-filter"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Suspended</p>
                <p className="text-2xl font-bold text-gray-600">{counts.suspended}</p>
              </div>
              <Ban className="w-8 h-8 text-gray-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <Input
          placeholder="Search vendors by name, email, or phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="vendor-search"
        />
      </div>

      {/* Vendors List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-green-600" />
        </div>
      ) : filteredVendors.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Users className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No vendors found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredVendors.map((vendor) => (
            <Card key={vendor.id} className="overflow-hidden" data-testid={`vendor-card-${vendor.id}`}>
              <CardContent className="p-0">
                {/* Vendor Header */}
                <div 
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedVendor(expandedVendor === vendor.id ? null : vendor.id)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
                      <Store className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{vendor.business_name}</h3>
                      <p className="text-sm text-gray-500">{vendor.owner_name}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {/* Commission Badge */}
                    {vendor.status === 'approved' && (
                      <Badge className="bg-blue-100 text-blue-800 border border-blue-200">
                        <Percent className="w-3 h-3 mr-1" />
                        {vendor.commission?.rate || 10}% Commission
                      </Badge>
                    )}
                    {getStatusBadge(vendor.status)}
                    {expandedVendor === vendor.id ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedVendor === vendor.id && (
                  <div className="border-t bg-gray-50 p-4">
                    <div className="grid md:grid-cols-3 gap-6">
                      {/* Contact Info */}
                      <div className="space-y-3">
                        <h4 className="font-medium text-gray-700 flex items-center gap-2">
                          <Building className="w-4 h-4" /> Contact Details
                        </h4>
                        <div className="space-y-2 text-sm">
                          <p className="flex items-center gap-2 text-gray-600">
                            <Phone className="w-4 h-4 text-gray-400" />
                            {vendor.phone}
                          </p>
                          <p className="flex items-center gap-2 text-gray-600">
                            <Mail className="w-4 h-4 text-gray-400" />
                            {vendor.email || 'Not provided'}
                          </p>
                          {vendor.address && (
                            <p className="flex items-start gap-2 text-gray-600">
                              <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                              <span>
                                {vendor.address.street}, {vendor.address.city}, {vendor.address.state} - {vendor.address.pincode}
                              </span>
                            </p>
                          )}
                        </div>
                      </div>

                      {/* KYC Info */}
                      <div className="space-y-3">
                        <h4 className="font-medium text-gray-700 flex items-center gap-2">
                          <CreditCard className="w-4 h-4" /> KYC Details
                        </h4>
                        <div className="space-y-2 text-sm">
                          <p className="text-gray-600">
                            <span className="text-gray-400">GST:</span> {vendor.kyc?.gst_number || 'Not provided'}
                          </p>
                          <p className="text-gray-600">
                            <span className="text-gray-400">PAN:</span> {vendor.kyc?.pan_number || 'Not provided'}
                          </p>
                          <p className="text-gray-600">
                            <span className="text-gray-400">Bank:</span> {vendor.kyc?.bank_account ? `****${vendor.kyc.bank_account.slice(-4)}` : 'Not provided'}
                          </p>
                        </div>
                      </div>

                      {/* Commission Settings */}
                      {vendor.status === 'approved' && (
                        <div className="space-y-3">
                          <h4 className="font-medium text-gray-700 flex items-center gap-2">
                            <Settings className="w-4 h-4" /> Commission Settings
                          </h4>
                          <div className="space-y-2 text-sm">
                            <p className="text-gray-600">
                              <span className="text-gray-400">Rate:</span> {vendor.commission?.rate || 10}%
                            </p>
                            <p className="text-gray-600">
                              <span className="text-gray-400">Min Payout:</span> ₹{vendor.commission?.min_payout || 500}
                            </p>
                            <p className="text-gray-600">
                              <span className="text-gray-400">Schedule:</span> {vendor.commission?.payout_schedule || 'Weekly'}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Rejection Reason */}
                    {vendor.status === 'rejected' && vendor.rejection_reason && (
                      <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-lg">
                        <p className="text-sm text-red-700">
                          <span className="font-medium">Rejection Reason:</span> {vendor.rejection_reason}
                        </p>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="mt-4 flex flex-wrap gap-2">
                      {vendor.status === 'pending' && (
                        <>
                          <Button
                            onClick={() => handleApprove(vendor.id)}
                            disabled={actionLoading === vendor.id}
                            className="bg-green-600 hover:bg-green-700"
                            data-testid={`approve-btn-${vendor.id}`}
                          >
                            {actionLoading === vendor.id ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <CheckCircle className="w-4 h-4 mr-2" />
                            )}
                            Approve Vendor
                          </Button>
                          <Button
                            variant="destructive"
                            onClick={() => setShowRejectModal(vendor.id)}
                            disabled={actionLoading === vendor.id}
                            data-testid={`reject-btn-${vendor.id}`}
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Reject
                          </Button>
                        </>
                      )}

                      {vendor.status === 'approved' && (
                        <>
                          <Button
                            variant="outline"
                            onClick={() => openSettingsModal(vendor)}
                            className="border-blue-200 text-blue-600 hover:bg-blue-50"
                            data-testid={`settings-btn-${vendor.id}`}
                          >
                            <Settings className="w-4 h-4 mr-2" />
                            Configure Settings
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => handleSuspend(vendor.id)}
                            disabled={actionLoading === vendor.id}
                            className="border-red-200 text-red-600 hover:bg-red-50"
                            data-testid={`suspend-btn-${vendor.id}`}
                          >
                            {actionLoading === vendor.id ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Ban className="w-4 h-4 mr-2" />
                            )}
                            Suspend Vendor
                          </Button>
                        </>
                      )}

                      {(vendor.status === 'rejected' || vendor.status === 'suspended') && (
                        <Button
                          onClick={() => handleApprove(vendor.id)}
                          disabled={actionLoading === vendor.id}
                          className="bg-green-600 hover:bg-green-700"
                          data-testid={`reactivate-btn-${vendor.id}`}
                        >
                          {actionLoading === vendor.id ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <CheckCircle className="w-4 h-4 mr-2" />
                          )}
                          Reactivate Vendor
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md bg-white shadow-2xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Reject Vendor
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">
                Please provide a reason for rejecting this vendor application. This will be shown to the vendor.
              </p>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter rejection reason..."
                className="w-full p-3 border rounded-lg min-h-[100px] focus:ring-2 focus:ring-red-200 focus:border-red-400"
                data-testid="reject-reason-input"
              />
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowRejectModal(null);
                    setRejectReason('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleReject(showRejectModal)}
                  disabled={actionLoading === showRejectModal || !rejectReason.trim()}
                  data-testid="confirm-reject-btn"
                >
                  {actionLoading === showRejectModal ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : null}
                  Confirm Rejection
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Settings Modal */}
      {showSettingsModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md bg-white shadow-2xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-blue-600" />
                Vendor Settings
              </CardTitle>
              <p className="text-sm text-gray-500 mt-1">
                Configure commission and payout settings for <span className="font-medium">{showSettingsModal.business_name}</span>
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Commission Rate */}
              <div className="space-y-2">
                <Label htmlFor="commission_rate" className="flex items-center gap-2">
                  <Percent className="w-4 h-4 text-gray-500" />
                  Commission Rate (%)
                </Label>
                <Input
                  id="commission_rate"
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={settingsData.commission_rate}
                  onChange={(e) => setSettingsData({ ...settingsData, commission_rate: e.target.value })}
                  data-testid="commission-rate-input"
                />
                <p className="text-xs text-gray-500">Platform fee deducted from each order</p>
              </div>

              {/* Minimum Payout */}
              <div className="space-y-2">
                <Label htmlFor="min_payout" className="flex items-center gap-2">
                  <IndianRupee className="w-4 h-4 text-gray-500" />
                  Minimum Payout Amount (₹)
                </Label>
                <Input
                  id="min_payout"
                  type="number"
                  min="0"
                  step="100"
                  value={settingsData.min_payout}
                  onChange={(e) => setSettingsData({ ...settingsData, min_payout: e.target.value })}
                  data-testid="min-payout-input"
                />
                <p className="text-xs text-gray-500">Minimum balance required to trigger payout</p>
              </div>

              {/* Payout Schedule */}
              <div className="space-y-2">
                <Label htmlFor="payout_schedule" className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-500" />
                  Payout Schedule
                </Label>
                <select
                  id="payout_schedule"
                  value={settingsData.payout_schedule}
                  onChange={(e) => setSettingsData({ ...settingsData, payout_schedule: e.target.value })}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-200"
                  data-testid="payout-schedule-select"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Bi-Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
                <p className="text-xs text-gray-500">How often payouts are processed</p>
              </div>

              {/* Preview */}
              <div className="p-3 bg-gray-50 rounded-lg border">
                <p className="text-sm font-medium text-gray-700 mb-2">Preview:</p>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>• Vendor keeps: <span className="font-medium text-green-600">{100 - settingsData.commission_rate}%</span> of each sale</p>
                  <p>• Payouts: <span className="font-medium">{settingsData.payout_schedule}</span> when balance ≥ <span className="font-medium">₹{settingsData.min_payout}</span></p>
                </div>
              </div>

              <div className="flex gap-2 justify-end pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowSettingsModal(null)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveSettings}
                  disabled={actionLoading === showSettingsModal.id}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="save-settings-btn"
                >
                  {actionLoading === showSettingsModal.id ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
